import * as React from 'react';

import { useLockFn, useRequest } from 'ahooks';
import { Button, Col, Empty, Flex, Form, Input, notification, Row } from 'antd';

import {
  CheckOutlinedIcon,
  PrimitiveCollapse,
  PrimitiveForm,
  PrimitiveSortableTreeSelect,
} from 'components';
import { getCategories, updatePageCategoryProducts } from 'services';
import type {
  PageCategoryProductsSetting,
  PageCategoryProductsSettings,
  PageSettingCode,
  TreeSelectSource,
} from 'types';
import {
  generateTreeSelectData,
  inputRule,
  localizePageSettingCode,
  normalizeToString,
} from 'utils';

type FieldType = {
  title: string;
  categoriesIds: TreeSelectSource[];
};

type PageBuilderCategoryProductsPanelProps = {
  code: PageSettingCode;
  setting: PageCategoryProductsSetting;
  treeData: TreeSelectSource[];
  treeLoading: boolean;
};

const PageBuilderCategoryProductsPanel = ({
  code,
  setting,
  treeData,
  treeLoading,
}: PageBuilderCategoryProductsPanelProps) => {
  const [form] = Form.useForm<FieldType>();

  const { title = '', categories = [] } = setting;
  const defaultCategories = categories.map(({ id, title }) => ({
    label: title,
    value: id,
  }));

  const onFinish = useLockFn(
    async ({ title = '', categoriesIds = [] }: FieldType) => {
      const ids = categoriesIds.map(({ value }) => normalizeToString(value));

      return updatePageCategoryProducts({ code, title, categoriesIds: ids })
        .then(() => {
          notification.success({
            message: `«${localizePageSettingCode(code).label}» با موفقیت ذخیره شد.`,
          });
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  return (
    <PrimitiveForm form={form} className='my-0' onFinish={onFinish}>
      <Row>
        <Col span={24}>
          <Form.Item<FieldType>
            label='عنوان'
            name='title'
            rules={[inputRule.required, inputRule.string]}
            initialValue={title}
          >
            <Input placeholder='عنوان این بخش را وارد کنید' />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item<FieldType>
            label='دسته‌بندی‌ها'
            name='categoriesIds'
            extra='با کشیدن می‌توانید ترتیب نمایش دسته‌بندی‌ها را تغییر دهید.'
            initialValue={defaultCategories}
          >
            <PrimitiveSortableTreeSelect
              placeholder='دسته‌بندی‌های این بخش را انتخاب کنید'
              loading={treeLoading}
              treeData={treeData}
            />
          </Form.Item>
        </Col>
      </Row>
      <Flex justify='end'>
        <Form.Item className='mb-0'>
          <Button type='primary' htmlType='submit' icon={<CheckOutlinedIcon />}>
            ذخیره تغییرات
          </Button>
        </Form.Item>
      </Flex>
    </PrimitiveForm>
  );
};

type PageBuilderCategoryProductsProps = {
  settings: PageCategoryProductsSettings;
};

const PageBuilderCategoryProducts = ({
  settings,
}: PageBuilderCategoryProductsProps) => {
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );

  const { categories = [] } = categoriesData ?? {};
  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );

  const positionOf = (code: string) =>
    Number(code.match(/_Pos(\d+)_/i)?.at(1) ?? 0);
  const entries = Object.entries(settings).sort(
    ([firstCode], [secondCode]) =>
      positionOf(firstCode) - positionOf(secondCode) ||
      firstCode.localeCompare(secondCode),
  );

  if (!entries.length) {
    return (
      <Empty
        className='py-10'
        description='هیچ بخشی برای محصولات دسته‌بندی وجود ندارد.'
      />
    );
  }

  return (
    <PrimitiveCollapse
      defaultActiveKey={entries.at(0)?.at(0) as string}
      items={entries.map(([code, setting]) => ({
        key: code,
        label: localizePageSettingCode(code).label,
        children: (
          <PageBuilderCategoryProductsPanel
            code={code}
            setting={setting}
            treeData={categoriesTreeData}
            treeLoading={categoriesLoading}
          />
        ),
      }))}
    />
  );
};

export { PageBuilderCategoryProducts };
